import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiCalendar,
  FiStar,
  FiEdit,
  FiCreditCard,
  FiVideo,
  FiMessageCircle,
  FiX,
  FiRepeat,
  FiFileText,
} from 'react-icons/fi';
import { Modal, Button, Form, Badge, Alert } from 'react-bootstrap';
import { generateCoachProfileUrl } from '../../utils/hashids';
import {
  API_BASE_URL,
  toastdisplay,
  createProfilePictureUser,
  getAuthHeaders,
} from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
import Sidebar from '../Sidebar';

import ProfilePicture from '../../components/ProfilePicture';
import './StudentDashboard.css';

const STATUS_LABELS = {
  CONFIRMED: 'CONFIRMED',
  AWAITING_PAYMENT: 'AWAITING_PAYMENT',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  NO_SHOW: 'NO_SHOW',
  PENDING: 'AWAITING_PAYMENT',
};

const normalizeStatus = (status) => {
  const raw = String(status || '').toUpperCase();
  if (raw === 'PENDING' || raw === 'VALIDATED') {
    return raw === 'VALIDATED' ? 'CONFIRMED' : 'AWAITING_PAYMENT';
  }
  return STATUS_LABELS[raw] || raw || 'CONFIRMED';
};

const StudentDashboard = ({ onMessageCoach } = {}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user] = useState(() => getStoredUser());

  // State management
  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [openDocumentReviews, setOpenDocumentReviews] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [actionBooking, setActionBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleAlternatives, setRescheduleAlternatives] = useState([]);
  const [rateData, setRateData] = useState({ rating: 5, comment: '' });

  // Filters
  const [bookingFilter, setBookingFilter] = useState('all');
  const [reviewFilter, setReviewFilter] = useState('all');

  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showEditReviewModal, setShowEditReviewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [editReviewData, setEditReviewData] = useState({
    rating: 5,
    comment: '',
  });

  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });

  useEffect(() => {
    loadDashboardData();
    loadConversations();
    loadDashboardStats();
  }, []);

  useEffect(() => {
    const rateId = Number(searchParams.get('rate') || 0);
    if (!rateId || !bookings.length) {
      return;
    }
    const match = bookings.find((booking) => booking.id === rateId);
    if (match && (match.can_review || match.pending_review)) {
      setActionBooking(match);
      setRateData({ rating: 5, comment: '' });
      setShowRateModal(true);
    }
  }, [bookings, searchParams]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadBookings(), loadReviews()]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toastdisplay('Failed to load dashboard data', false, 3000, setToast);
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/bookings?user_email=${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setBookings(Array.isArray(data) ? data : data.bookings || []);
        setOpenDocumentReviews(data.open_document_reviews || []);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/dashboard-stats?user_email=${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    }
  };

  const loadReviews = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/reviews?user_email=${encodeURIComponent(user.email)}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/conversations`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setConversations([]);
    }
  };

  const handleCompletePayment = (booking) => {
    if (booking.payment_link && booking.can_complete_payment) {
      // Redirect directly to the payment link
      window.open(booking.payment_link, '_blank');
      toastdisplay('Redirecting to payment...', true, 2000, setToast);
    } else {
      toastdisplay('Payment link not available', false, 3000, setToast);
    }
  };

  const handleEditReview = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/student/reviews/${selectedReview.id}`,
        {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            rating: editReviewData.rating,
            comment: editReviewData.comment,
            student_email: user.email,
          }),
        }
      );

      if (response.ok) {
        setReviews((prevReviews) =>
          prevReviews.map((review) =>
            review.id === selectedReview.id
              ? { ...review, ...editReviewData }
              : review
          )
        );

        setShowEditReviewModal(false);
        setSelectedReview(null);
        toastdisplay('Review updated successfully!', true, 3000, setToast);
      } else {
        const errorData = await response.json();
        toastdisplay(
          errorData.detail || 'Failed to update review',
          false,
          3000,
          setToast
        );
      }
    } catch (error) {
      console.error('Error updating review:', error);
      toastdisplay('Failed to update review', false, 3000, setToast);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const normalized = normalizeStatus(status);
    const statusConfig = {
      CONFIRMED: { text: 'CONFIRMED', className: 'status-badge confirmed' },
      AWAITING_PAYMENT: {
        text: 'AWAITING_PAYMENT',
        className: 'status-badge pending',
      },
      COMPLETED: { text: 'COMPLETED', className: 'status-badge completed' },
      CANCELLED: { text: 'CANCELLED', className: 'status-badge cancelled' },
      NO_SHOW: { text: 'NO_SHOW', className: 'status-badge cancelled' },
    };
    const config = statusConfig[normalized] || statusConfig.CONFIRMED;
    return <span className={config.className}>{config.text}</span>;
  };

  const handleCancelBooking = async () => {
    if (!actionBooking) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/student/booking/${actionBooking.id}/cancel`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            student_email: user.email,
            cancellation_reason: cancelReason,
            request_refund: true,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Could not cancel');
      }
      toastdisplay(data.message || 'Session cancelled', true, 4000, setToast);
      setShowCancelModal(false);
      setCancelReason('');
      setActionBooking(null);
      await loadBookings();
    } catch (error) {
      toastdisplay(error.message, false, 4000, setToast);
    } finally {
      setLoading(false);
    }
  };

  const handleReschedule = async (date = rescheduleDate, time = rescheduleTime) => {
    if (!actionBooking || !date || !time) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/student/booking/${actionBooking.id}/reschedule`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            student_email: user.email,
            booking_date: date,
            start_time: time,
          }),
        }
      );
      const data = await response.json();
      if (response.status === 409) {
        const detail = data.detail || {};
        setRescheduleAlternatives(detail.alternatives || []);
        toastdisplay(
          detail.message || 'This time is taken — pick another',
          false,
          4000,
          setToast
        );
        return;
      }
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Could not reschedule');
      }
      toastdisplay(data.message || 'Session moved', true, 3000, setToast);
      setShowRescheduleModal(false);
      setRescheduleAlternatives([]);
      setActionBooking(null);
      await loadBookings();
    } catch (error) {
      toastdisplay(error.message, false, 4000, setToast);
    } finally {
      setLoading(false);
    }
  };

  const handleRateSession = async () => {
    if (!actionBooking) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/coach/reviews?student_email=${encodeURIComponent(user.email)}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            booking_id: actionBooking.id,
            rating: rateData.rating,
            comment: rateData.comment,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Could not submit review');
      }
      toastdisplay('Thanks for the review', true, 3000, setToast);
      setShowRateModal(false);
      setActionBooking(null);
      await Promise.all([loadBookings(), loadReviews()]);
    } catch (error) {
      toastdisplay(error.message, false, 4000, setToast);
    } finally {
      setLoading(false);
    }
  };

  const messageCoach = (booking) => {
    if (onMessageCoach) {
      onMessageCoach({
        email: booking.coach_email,
        first_name: (booking.coach_name || '').split(' ')[0],
        last_name: (booking.coach_name || '').split(' ').slice(1).join(' '),
        profile_picture: booking.coach_picture,
      });
      return;
    }
    navigate(generateCoachProfileUrl(booking.coach_id));
  };

  const cancelCopy = (booking) => {
    if (!booking) return '';
    const when = new Date(`${booking.booking_date}T${booking.start_time}:00`);
    const hours = (when.getTime() - Date.now()) / 36e5;
    if (hours < 24) {
      return 'This is within 24 hours of the session. You can still cancel so the slot is freed, but there is no refund.';
    }
    return 'More than 24 hours ahead: if you paid, a refund can be processed.';
  };

  // Helper functions for date/time formatting with timezone
  const formatDateWithTimezone = (dateString, timezone = null) => {
    const date = new Date(dateString);
    // Use user's local timezone if coach timezone is not available
    const displayTimezone =
      timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: displayTimezone,
    });
  };

  const formatTimeWithTimezone = (timeString, timezone = null) => {
    try {
      // Create a date object for the time (using arbitrary date)
      const timeDate = new Date(`2000-01-01T${timeString}`);
      const displayTimezone =
        timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

      return timeDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: displayTimezone,
      });
    } catch (error) {
      return timeString; // fallback to original string if parsing fails
    }
  };

  const formatTimezoneDisplay = (timezone) => {
    if (!timezone) return 'Local Time';

    const timezoneMap = {
      'Europe/Paris': 'CET',
      'Europe/London': 'GMT',
      'America/New_York': 'ET',
      'America/Los_Angeles': 'PT',
      'America/Chicago': 'CT',
      'Asia/Tokyo': 'JST',
      'Asia/Shanghai': 'CST',
      'Australia/Sydney': 'AET',
      UTC: 'UTC',
    };

    return timezoneMap[timezone] || timezone.split('/').pop();
  };

  // Update the existing formatDate and formatTime functions
  const formatDate = (dateString, timezone = null) => {
    return formatDateWithTimezone(dateString, timezone);
  };

  const formatTime = (timeString, timezone = null) => {
    return formatTimeWithTimezone(timeString, timezone);
  };

  // Get student's timezone
  const getStudentTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  // Convert time from coach timezone to student timezone
  const convertToStudentTime = (coachTime, date, coachTimezone) => {
    const studentTimezone = getStudentTimezone();

    if (!coachTimezone) {
      return { studentTime: coachTime, isDifferent: false };
    }

    if (coachTimezone === studentTimezone) {
      return { studentTime: coachTime, isDifferent: false };
    }

    try {
      // Get timezone offset for each timezone on this date
      const getTimezoneOffsetMinutes = (tz) => {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          timeZoneName: 'shortOffset',
        });
        const parts = formatter.formatToParts(new Date(`${date}T12:00:00Z`));
        const tzPart = parts.find((p) => p.type === 'timeZoneName');
        const offsetString = tzPart?.value;

        const match = offsetString?.match(/GMT([+-])?(\d+)?(?::(\d+))?/);
        if (!match) {
          return 0;
        }
        const sign = match[1] === '-' ? -1 : 1;
        const hours = parseInt(match[2] || '0');
        const mins = parseInt(match[3] || '0');
        const totalMins = sign * (hours * 60 + mins);
        return totalMins;
      };

      const coachOffsetMins = getTimezoneOffsetMinutes(coachTimezone);
      const studentOffsetMins = getTimezoneOffsetMinutes(studentTimezone);
      const offsetDiff = studentOffsetMins - coachOffsetMins;

      // Parse coach time and apply offset
      const [h, m] = coachTime.split(':').map(Number);
      let studentMins = h * 60 + m + offsetDiff;

      // Normalize to 0-1439 range
      while (studentMins < 0) studentMins += 1440;
      while (studentMins >= 1440) studentMins -= 1440;

      const studentHours = Math.floor(studentMins / 60);
      const studentMinutes = studentMins % 60;
      const studentTimeString = `${String(studentHours).padStart(2, '0')}:${String(studentMinutes).padStart(2, '0')}`;

      return {
        studentTime: studentTimeString,
        studentTimezone,
        isDifferent: true,
      };
    } catch (error) {
      console.error('❌ Error converting timezone:', error);
      return { studentTime: coachTime, isDifferent: false };
    }
  };

  // Check if a booking is in the past
  const isBookingInPast = (bookingDate, startTime) => {
    try {
      const bookingDateTime = new Date(`${bookingDate}T${startTime}:00`);
      const now = new Date();
      return bookingDateTime < now;
    } catch (error) {
      return false;
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <FiStar
        key={index}
        size={16}
        style={{
          color: index < rating ? '#ffc107' : '#e9ecef',
          fill: index < rating ? '#ffc107' : 'none',
        }}
      />
    ));
  };

  // Filter functions
  const getFilteredBookings = () => {
    if (bookingFilter === 'all') return bookings;
    return bookings.filter((booking) => booking.status === bookingFilter);
  };

  const getFilteredReviews = () => {
    if (reviewFilter === 'all') return reviews;
    return reviews.filter((review) => review.status === reviewFilter);
  };

  const renderBookingCard = (booking, { past = false } = {}) => {
    const converted = convertToStudentTime(
      booking.start_time,
      booking.booking_date,
      booking.coach_timezone
    );
    const studentTimezone = getStudentTimezone();
    const status = normalizeStatus(booking.status);
    return (
      <div
        key={booking.id}
        className={`booking-card${past ? ' booking-past' : ''}`}
      >
        <div className="booking-card-header">
          <span className="booking-service-title">{booking.service_title}</span>
          <span className="booking-price">€{booking.price_paid}</span>
        </div>
        <div className="booking-card-status-row">{getStatusBadge(status)}</div>
        <div className="booking-card-details">
          <FiCalendar className="booking-card-icon" />
          <span className="booking-card-info">
            {formatDate(booking.booking_date)} • {formatTime(converted.studentTime)}
            <span className="timezone-info">
              {' '}
              (
              {converted.isDifferent
                ? formatTimezoneDisplay(studentTimezone)
                : booking.coach_timezone
                  ? formatTimezoneDisplay(booking.coach_timezone)
                  : 'Local Time'}
              )
            </span>
            {converted.isDifferent && (
              <span className="coach-time-note">
                {' '}
                • {formatTime(booking.start_time)}{' '}
                {formatTimezoneDisplay(booking.coach_timezone)} (coach)
              </span>
            )}
            <br />
            with{' '}
            <span className="booking-coach-name">{booking.coach_name}</span>
          </span>
        </div>
        <div className="booking-card-actions">
          {status === 'AWAITING_PAYMENT' && booking.can_complete_payment && (
            <button
              className="btn-lwz-primary"
              onClick={() => handleCompletePayment(booking)}
            >
              <FiCreditCard style={{ marginRight: '8px' }} />
              Finish payment
            </button>
          )}
          {status === 'CONFIRMED' && booking.meeting_link && (
            <a
              className="btn-lwz-primary"
              href={booking.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiVideo style={{ marginRight: '8px' }} />
              Join meeting
            </a>
          )}
          {status === 'CONFIRMED' && (
            <>
              <button
                className="btn-outline"
                onClick={() => messageCoach(booking)}
              >
                <FiMessageCircle style={{ marginRight: '6px' }} />
                Message
              </button>
              <button
                className="btn-outline"
                onClick={() => {
                  setActionBooking(booking);
                  setRescheduleDate(booking.booking_date);
                  setRescheduleTime(booking.start_time);
                  setRescheduleAlternatives([]);
                  setShowRescheduleModal(true);
                }}
              >
                <FiRepeat style={{ marginRight: '6px' }} />
                Reschedule
              </button>
              <button
                className="btn-outline"
                onClick={() => {
                  setActionBooking(booking);
                  setCancelReason('');
                  setShowCancelModal(true);
                }}
              >
                <FiX style={{ marginRight: '6px' }} />
                Cancel
              </button>
            </>
          )}
          {(booking.can_review || booking.pending_review) && (
            <button
              className="btn-lwz-primary"
              onClick={() => {
                setActionBooking(booking);
                setRateData({ rating: 5, comment: '' });
                setShowRateModal(true);
              }}
            >
              <FiStar style={{ marginRight: '6px' }} />
              Rate this session
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderBookingsTab = () => {
    const awaitingPayment = bookings.filter(
      (booking) => normalizeStatus(booking.status) === 'AWAITING_PAYMENT'
    );
    const upcoming = bookings
      .filter((booking) => {
        const status = normalizeStatus(booking.status);
        return (
          (status === 'CONFIRMED' || status === 'AWAITING_PAYMENT') &&
          !isBookingInPast(booking.booking_date, booking.start_time)
        );
      })
      .sort(
        (a, b) =>
          new Date(`${a.booking_date}T${a.start_time}`) -
          new Date(`${b.booking_date}T${b.start_time}`)
      );
    const needsReview = bookings.filter(
      (booking) => booking.can_review || booking.pending_review
    );
    const past = bookings.filter((booking) => {
      const status = normalizeStatus(booking.status);
      return (
        status === 'COMPLETED' ||
        status === 'CANCELLED' ||
        status === 'NO_SHOW' ||
        isBookingInPast(booking.booking_date, booking.start_time)
      );
    });

    return (
      <div>
        {awaitingPayment.length > 0 && (
          <Alert variant="warning" className="student-awaiting-banner">
            <strong>Awaiting payment</strong>
            <p className="mb-2">
              {awaitingPayment.length} session
              {awaitingPayment.length > 1 ? 's' : ''} still need payment to stay
              reserved.
            </p>
            <button
              className="btn-lwz-primary"
              onClick={() => handleCompletePayment(awaitingPayment[0])}
            >
              <FiCreditCard style={{ marginRight: '8px' }} />
              Pay now
            </button>
          </Alert>
        )}

        {openDocumentReviews.length > 0 && (
          <button
            type="button"
            className="student-doc-review-card"
            onClick={() => navigate('/applications')}
          >
            <FiFileText />
            <span>
              A document review is in progress. Open your application package.
            </span>
          </button>
        )}

        <h4 className="student-section-title">Upcoming</h4>
        {upcoming.length === 0 ? (
          <div className="empty-state">
            <FiCalendar size={36} />
            <h4>No upcoming sessions</h4>
            <p>Book a coach from the marketplace.</p>
          </div>
        ) : (
          upcoming.map((booking) => renderBookingCard(booking))
        )}

        {needsReview.length > 0 && (
          <>
            <h4 className="student-section-title">Rate a completed session</h4>
            {needsReview.map((booking) => renderBookingCard(booking))}
          </>
        )}

        {past.length > 0 && (
          <div className="student-past-wrap">
            <button
              type="button"
              className="btn-outline"
              onClick={() => setShowPast((value) => !value)}
            >
              {showPast ? 'Hide past' : `Past sessions (${past.length})`}
            </button>
            {showPast
              ? past.map((booking) => renderBookingCard(booking, { past: true }))
              : null}
          </div>
        )}
      </div>
    );
  };

  const renderReviewsTab = () => {
    const filteredReviews = getFilteredReviews();

    return (
      <div>
        <div className="filter-section">
          <Form.Select
            value={reviewFilter}
            onChange={(e) => setReviewFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Reviews</option>
            <option value="published">Published</option>
            <option value="pending">Pending</option>
          </Form.Select>
        </div>

        {filteredReviews.length === 0 ? (
          <div className="empty-state">
            <FiStar size={48} />
            <h4>No reviews yet</h4>
            <p>Leave a review after your coaching session</p>
            <button
              className="btn-primary"
              onClick={() => navigate('/coaching')}
            >
              Find Coaches
            </button>
          </div>
        ) : (
          filteredReviews.map((review) => (
            <div key={review.id} className="review-card">
              <div className="review-card-header">
                <ProfilePicture
                  user={createProfilePictureUser(review.coach)}
                  alt={review.coach_name}
                  className="review-coach-avatar"
                  context="Review list"
                />
                <div className="review-coach-info">
                  <span className="review-coach-name">{review.coach_name}</span>
                  <div className="review-stars">
                    {renderStars(review.rating)}
                  </div>
                </div>
                <Badge
                  bg={review.is_published ? 'success' : 'warning'}
                  className="review-status-badge"
                >
                  {review.is_published ? 'Published' : 'Pending'}
                </Badge>
              </div>
              <div className="review-content-block">
                <div className="review-coach-label">
                  <strong>Review gave to coach: {review.coach_name}</strong>
                </div>
                <p>{review.comment}</p>
              </div>
              <div className="review-meta-row">
                <span className="review-date">
                  {review.created_at
                    ? formatDate(review.created_at)
                    : 'Recently'}
                </span>
                <button
                  className="btn-outline review-edit-btn"
                  onClick={() => {
                    setSelectedReview(review);
                    setEditReviewData({
                      rating: review.rating,
                      comment: review.comment,
                    });
                    setShowEditReviewModal(true);
                  }}
                >
                  <FiEdit />
                  Edit Review
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderEditReviewModal = () => (
    <Modal
      show={showEditReviewModal}
      onHide={() => {
        setShowEditReviewModal(false);
        setSelectedReview(null);
      }}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Review</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label>Rating</Form.Label>
            <div>
              {[1, 2, 3, 4, 5].map((star) => (
                <FiStar
                  key={star}
                  size={24}
                  style={{
                    cursor: 'pointer',
                    color:
                      star <= editReviewData.rating ? '#ffc107' : '#e9ecef',
                    fill: star <= editReviewData.rating ? '#ffc107' : 'none',
                    marginRight: '4px',
                  }}
                  onClick={() =>
                    setEditReviewData({ ...editReviewData, rating: star })
                  }
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group>
            <Form.Label>Review Comment</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={editReviewData.comment}
              onChange={(e) =>
                setEditReviewData({
                  ...editReviewData,
                  comment: e.target.value,
                })
              }
              placeholder="Share your experience with this coach..."
              maxLength={500}
            />
            <Form.Text>
              {editReviewData.comment.length}/500 characters
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={() => {
            setShowEditReviewModal(false);
            setSelectedReview(null);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleEditReview}
          disabled={loading || !editReviewData.comment.trim()}
        >
          {loading ? 'Updating...' : 'Update Review'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="student-dashboard">
      <div className="student-dashboard-header">
        <h3>My sessions</h3>
      </div>

      <div className="dashboard-tabs">
        <div className="nav-tabs">
          <button
            className={`nav-link ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            <FiCalendar />
            Bookings
          </button>
          <button
            className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            <FiStar />
            Reviews
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'bookings' && renderBookingsTab()}
        {activeTab === 'reviews' && renderReviewsTab()}
      </div>

      {renderEditReviewModal()}

      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{cancelCopy(actionBooking)}</p>
          <Form.Control
            as="textarea"
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Optional reason for the coach"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCancelModal(false)}>
            Keep session
          </Button>
          <Button variant="danger" onClick={handleCancelBooking} disabled={loading}>
            {loading ? 'Cancelling...' : 'Cancel session'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRescheduleModal}
        onHide={() => setShowRescheduleModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Propose another time</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Pick a new slot for the same service, or open the coach profile to
            browse availability.
          </p>
          <Form.Group className="mb-2">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Time</Form.Label>
            <Form.Control
              type="time"
              value={rescheduleTime}
              onChange={(e) => setRescheduleTime(e.target.value)}
            />
          </Form.Group>
          {rescheduleAlternatives.length > 0 ? (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {rescheduleAlternatives.map((slot) => (
                <Button
                  key={`${slot.date}-${slot.start}`}
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    setRescheduleDate(slot.date);
                    setRescheduleTime(slot.start);
                    handleReschedule(slot.date, slot.start);
                  }}
                >
                  {slot.date} {slot.start}
                </Button>
              ))}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() =>
              navigate(
                `${generateCoachProfileUrl(actionBooking?.coach_id)}?service_id=${actionBooking?.service_id}`
              )
            }
          >
            Open coach profile
          </Button>
          <Button
            className="btn-lwz-primary"
            onClick={() => handleReschedule()}
            disabled={loading || !rescheduleDate || !rescheduleTime}
          >
            {loading ? 'Moving...' : 'Move session'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRateModal} onHide={() => setShowRateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Rate this session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <FiStar
                key={star}
                size={24}
                style={{
                  cursor: 'pointer',
                  color: star <= rateData.rating ? '#ffc107' : '#e9ecef',
                  fill: star <= rateData.rating ? '#ffc107' : 'none',
                  marginRight: '4px',
                }}
                onClick={() => setRateData({ ...rateData, rating: star })}
              />
            ))}
          </div>
          <Form.Control
            as="textarea"
            rows={4}
            value={rateData.comment}
            onChange={(e) => setRateData({ ...rateData, comment: e.target.value })}
            placeholder="What helped, and what should change?"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowRateModal(false)}>
            Later
          </Button>
          <Button
            className="btn-lwz-primary"
            onClick={handleRateSession}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Submit review'}
          </Button>
        </Modal.Footer>
      </Modal>

      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
